import * as R from 'ramda';
import { connect } from 'react-redux';
import React, { useState } from 'react';
import { compose, withHandlers } from 'react-recompose';
// mui
import Menu from '@mui/material/Menu';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
// components
import { TextComponent } from '../../text';
import { ActionBox } from '../../action-box';
import EditableBox from '../../editable-box';
// features
import { setExpandedContainerOptions } from '../../../features/expanded-container/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { getFreightClassOptions } from '../../../helpers/options';
// icons
import * as I from '../../../svgs';
// ui
import { Flex, StyledLink } from '../../../ui';
// feature table
import { TableCell, FlexContainer } from '../ui';
//////////////////////////////////////////////////

const blueColor = G.getTheme('colors.blueColor');
const mainBlueColor = G.getTheme('colors.mainBlue');

export const EditableCell = (props) => {
  const {
    data,
    field,
    value,
    width,
    settings,
    callbackData,
  } = props;

  const {
    getValue,
    allowEmpty,
    removeTitle,
    placeholder,
    handleRemove,
    handleUpdate,
  } = settings;

  const valueToUse = G.isFunction(getValue)
    ? getValue(value, data, callbackData)
    : R.or(value, '');

  const handleRemoveCallback = G.isFunction(handleRemove)
    ? () => handleRemove(data, callbackData)
    : null;

  const handleUpdateCallback = G.isFunction(handleUpdate)
    ? (updatedValue) => handleUpdate(updatedValue, data, callbackData)
    : null;

  return (
    <TableCell width={width} minWidth={width} p={R.pathOr('8px', ['p'], field)}>
      <Flex width='100%' height='100%'>
        {
          G.isNotNilAndNotEmpty(valueToUse) &&
          G.isFunction(handleRemove) &&
          <Flex
            mr={10}
            flexShrink={0}
            cursor='pointer'
            onClick={handleRemoveCallback}
            title={R.or(removeTitle, G.getWindowLocale('actions:remove', 'Remove'))}
          >
            {I.trash(mainBlueColor)}
          </Flex>
        }
        <EditableBox
          value={valueToUse}
          allowEmpty={allowEmpty}
          placeholder={placeholder}
          onBlurCallback={handleUpdateCallback}
          wrapperStyles={{ flex: 1, minWidth: 0, height: '100%' }}
          textareaStyles={{ fontSize: 12, height: '100%', color: G.getTheme('colors.dark.mainDark') }}
          boxStyles={{ fontSize: 12, height: '100%', display: 'flex', overflow: 'auto', alignItems: 'center' }}
        />
      </Flex>
    </TableCell>
  );
};

export const BooleanElement = ({ value, width, field }) => (
  <TableCell
    width={width}
    minWidth={width}
    p={R.or(field.p, '8px')}
  >
    <FlexContainer height='100%' justify='center' direction='column'>
      {
        G.ifElse(
          G.isTrue(value),
          I.uiTrue,
          I.uiFalse,
        )()
      }
    </FlexContainer>
  </TableCell>
);

const renderEnumValue = (value) => {
  if (G.isArray(value)) {
    return R.compose(
      R.join(', '),
      R.map(G.getEnumLocale),
    )(value);
  }

  return G.getEnumLocale(value);
};

export const EnumElement = ({ value, width, field }) => {
  const text = renderEnumValue(value);

  return (
    <TableCell
      width={width}
      minWidth={width}
      p={R.or(field.p, '8px')}
    >
      <FlexContainer height='100%' justify='center' direction='column'>
        <TextComponent
          title={text}
          display='block'
          overflow='hidden'
          withEllipsis={true}
          maxWidth={`calc(${width} - 8px)`}
        >
          {text}
        </TextComponent>
      </FlexContainer>
    </TableCell>
  );
};

const getFreightClassValues = (data) => {
  let dataArray = data;

  if (G.isString(data)) dataArray = R.of(Array, data);

  if (G.isNilOrEmpty(dataArray)) return '';

  return R.compose(
    R.join(', '),
    R.values,
    R.pick(dataArray),
    R.map(R.prop('label')),
    R.indexBy(R.prop(GC.FIELD_VALUE)),
  )(getFreightClassOptions());
};

export const FreightClassElement = ({ value, width, field }) => (
  <TableCell
    width={width}
    minWidth={width}
    p={R.or(field.p, '8px')}
  >
    <FlexContainer height='100%' justify='center' direction='column'>
      {getFreightClassValues(value)}
    </FlexContainer>
  </TableCell>
);

export const ActionCell = ({ text, action, width, field }) => (
  <TableCell
    width={width}
    minWidth={width}
    p={R.or(field.p, '8px')}
  >
    <FlexContainer height='100%' justify='center' direction='column'>
      <ActionBox text={text} action={action} />
    </FlexContainer>
  </TableCell>
);

const enhance = compose(
  connect(null, { setExpandedContainerOptions }),
  withHandlers({
    handleOpenExpandedContainer: (props) => () => {
      const { rowGuid, componentType, visitPageGuid, setExpandedContainerOptions } = props;

      setExpandedContainerOptions({
        opened: true,
        componentType,
        visitPageGuid,
        options: { rowGuid },
      });
    },
  }),
);

export const ActionCellWithExpandedContainer = enhance(({
  text,
  width,
  field,
  handleOpenExpandedContainer,
}) => (
  <TableCell
    width={width}
    minWidth={width}
    p={R.or(field.p, '8px')}
  >
    <FlexContainer height='100%' justify='center' direction='column'>
      <ActionBox text={text} action={handleOpenExpandedContainer} />
    </FlexContainer>
  </TableCell>
));

const convertors = {
  [GC.FIELD_WEIGHT]: (name) => G.fromKgsToPounds(name, 2),
  [GC.FIELD_DISTANCE]: (name) => G.fromKmsToMiles(name, 2),
  [GC.FIELD_ITEM_VOLUME]: (name) => G.fromCubicMeterToFeet(name, 2),
  [GC.FIELD_TEMPERATURE]: (name) => G.fromCelsiusToFahrenheit(name, 2),
};

const getValue = ({ type, uomSystem, fieldData, withoutConversion }) => {
  if (R.or(G.isTrue(withoutConversion), R.equals(uomSystem, GC.METRIC_SYSTEM))) return G.mathRoundNumber(fieldData);

  return convertors[type](fieldData);
};

const getCollectionValue = ({ type, uomSystem, fieldData }) => R.compose(
    R.join(', '),
    R.map((item) => getValue({ type, uomSystem, fieldData: item })),
  )(R.or(fieldData, []));

const metricUOMs = {
  [GC.FIELD_WEIGHT]: GC.UOM_KILOGRAM,
  [GC.FIELD_TEMPERATURE]: GC.UOM_CELSIUS,
  [GC.FIELD_DISTANCE]: GC.UOM_KILOMETER_LABEL,
  [GC.FIELD_ITEM_VOLUME]: GC.UOM_CUBIC_METERS,
};

const imperialUOMs = {
  [GC.FIELD_WEIGHT]: GC.UOM_POUND,
  [GC.FIELD_DISTANCE]: GC.UOM_MILE_LABEL,
  [GC.FIELD_ITEM_VOLUME]: GC.UOM_CUBIC_FEET,
  [GC.FIELD_TEMPERATURE]: GC.UOM_FAHRENHEIT,
};

export const UomRelatedField = ({ type, width, field, fieldData, withoutConversion }) => {
  const uomSystem = G.getConfigGeneralUomCalcDefaultUomSystemFromWindow();

  const collection = R.path(['collection'], field);

  const getValueFunc = G.ifElse(G.isTrue(collection), getCollectionValue, getValue);

  const value = getValueFunc({ type, uomSystem, fieldData, withoutConversion });
  const uom = G.ifElse(R.equals(uomSystem, GC.METRIC_SYSTEM), metricUOMs[type], imperialUOMs[type]);

  const text = `${value} ${uom}`;

  return (
    <TableCell
      width={width}
      minWidth={width}
      p={R.or(field.p, '8px')}
    >
      <FlexContainer height='100%' justify='center' direction='column'>
        <TextComponent title={text} maxWidth='100%' withEllipsis={true}>
          {text}
        </TextComponent>
      </FlexContainer>
    </TableCell>
  );
};

export const CurrencyField = (props) => {
  const { width, value, callbackData, field: { p } } = props;

  const currency = R.pathOr(
    R.or(G.getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_DEFAULT_CURRENCY), GC.DEFAULT_UI_CURRENCY),
    [GC.FIELD_CURRENCY],
    callbackData,
  );

  const text = G.ifElse(G.isNilOrEmpty(value), '', `${G.getCurrencySymbol(currency)} ${G.mathRoundNumber(value)}`);

  return (
    <TableCell
      width={width}
      minWidth={width}
      p={R.or(p, '8px')}
    >
      <FlexContainer height='100%' justify='center' direction='column'>
        <TextComponent
          title={text}
          display='block'
          overflow='hidden'
          withEllipsis={true}
          maxWidth={`calc(${width} - 8px)`}
        >
          {text}
        </TextComponent>
      </FlexContainer>
    </TableCell>
  );
};

export const BranchElement = (props) => {
  const { data, value, width, field } = props;

  const branchGuid = R.or(value, R.path([GC.FIELD_BRANCH_GUID], data));

  const title = G.getBranchNameFromWindowByGuid(branchGuid);

  return (
    <TableCell
      width={width}
      minWidth={width}
      p={R.or(field.p, '8px')}
    >
      {
        title &&
        <FlexContainer height='100%' justify='center' direction='column'>
          <TextComponent title={title} maxWidth='100%' withEllipsis={true}>
            {title}
          </TextComponent>
        </FlexContainer>
      }
    </TableCell>
  );
};

export const ClickableLink = (props) => {
  const { value, width, field, settings } = props;

  const label = R.pathOr('actions:show', [GC.FIELD_LABEL], settings);
  const title = G.getWindowLocale(label, 'Show');

  return (
    <TableCell
      width={width}
      display='flex'
      minWidth={width}
      alignItems='center'
      p={R.or(field.p, '8px')}
    >
      {
        G.isNotNilAndNotEmpty(value) &&
        <StyledLink
          mr='6px'
          href={value}
          target='_blank'
          maxWidth='100%'
          color={blueColor}
          wordBreak='break-word'
        >
          {title}
        </StyledLink>
      }
    </TableCell>
  );
};

export const SelectDropdownMuiCell = (props) => {
  const { data, value, width, field, settings, callbackData } = props;

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => setAnchorEl(null);
  const handleClick = ({ currentTarget }) => setAnchorEl(currentTarget);

  const handleSelect = (value) => {
    const selectAction = G.getPropFromObject('selectAction', settings);

    if (G.isFunction(selectAction)) selectAction(value, data, callbackData);

    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const options = R.pathOr([], ['selectOptions'], settings);
  const fieldName = G.getPropFromObject(GC.FIELD_NAME, field);
  const title = G.ifElse(R.equals(fieldName, GC.FIELD_STATUS), G.getEnumLocale(value), value);

  let permissions = R.pathOr([], ['selectPermissions'], settings);

  if (G.isFunction(permissions)) permissions = permissions(data, callbackData);

  const textProps = {
    title,
    maxWidth: '100%',
    withEllipsis: true,
  };

  const tableCellPadding = G.getPropFromObject('p', field);

  const tableCellStyles = {
    width,
    display: 'flex',
    minWidth: width,
    p: R.or(tableCellPadding, '8px'),
  };

  const { editable, disabled } = data;

  if (G.isAnyTrue(
    G.isTrue(disabled),
    G.isFalse(editable),
    R.and(G.isNotNilAndNotEmpty(permissions), G.hasNotAmousCurrentUserPermissions(permissions)),
  )) {
    return (
      <TableCell {...tableCellStyles}>
        <TextComponent {...textProps}>
          {title}
        </TextComponent>
      </TableCell>
    );
  }

  return (
    <TableCell {...tableCellStyles} p={R.or(tableCellPadding, '8px 0px')}>
      <Button
        aria-haspopup='true'
        disableRipple={true}
        onClick={handleClick}
        id='select-dropdown-mui-button'
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'select-dropdown-mui-cell' : undefined}
        sx={{ position: 'static', fontSize: 'inherit', textTransform: 'none', color: G.getTheme('colors.light.blue') }}
      >
        <TextComponent {...textProps}>
          {title}
        </TextComponent>
      </Button>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        id='select-dropdown-mui-button-menu'
        slotProps={{ paper: { style: { maxHeight: 155 } } }}
        MenuListProps={{ 'aria-labelledby': 'select-dropdown-mui-button' }}
      >
        {
          G.isNotNilAndNotEmpty(options) &&
          options.map(({ value, label }) => (
            <MenuItem key={value} onClick={() => handleSelect(value)}>
              {label}
            </MenuItem>
          ))
        }
      </Menu>
    </TableCell>
  );
};

export const LocationComponent = ({ value, width, field }) => (
  <TableCell
    width={width}
    minWidth={width}
    p={R.or(field.p, '8px')}
  >
    {G.concatLocationFields(value)}
  </TableCell>
);

export const TotalField = ({ data, width, value, field: { p }, settings }) => {
  const currencyKey = R.propOr(GC.FIELD_CURRENCY, 'currencyKey', settings);
  const text = `${G.getCurrencySymbol(G.getPropFromObject(currencyKey, data))} ${G.toFixed(R.or(value, 0))}`;

  return (
    <TableCell width={width} minWidth={width} p={R.or(p, '8px')}>
      <TextComponent
        p='4px 6px'
        title={text}
        display='block'
        fontWeight={700}
        overflow='hidden'
        borderRadius='3px'
        width='fit-content'
        withEllipsis={true}
        maxWidth={R.subtract(width, 8)}
        bg={G.getTheme('colors.light.blue')}
        color={G.getTheme('colors.light.mainLight')}
      >
        {text}
      </TextComponent>
    </TableCell>
  );
};

export const StateElement = ({ value, width, field }) => {
  const text = G.isArray(value) ?
    R.compose(
      R.join(', '),
      R.map(G.getStateValueByConfig),
    )(value) :
    G.getStateValueByConfig(value);

  return (
    <TableCell
      width={width}
      minWidth={width}
      p={R.or(field.p, '8px')}
    >
      <FlexContainer height='100%' justify='center' direction='column'>
        <TextComponent
          title={text}
          display='block'
          overflow='hidden'
          withEllipsis={true}
          maxWidth={`calc(${width} - 8px)`}
        >
          {text}
        </TextComponent>
      </FlexContainer>
    </TableCell>
  );
};
